import { c as _c } from "react/compiler-runtime";
import { type ComponentProps } from "react";
import { cn } from "@local/utils/src/cn";
import { Link } from "../ui/Link";
import { Pill } from "./Pill";
type Variant = "default" | "primary" | "outline" | "ghost" | "link" | "lightGray" | "urgent" | "amber" | "subtleGrey" | "expandContent";
type Size = "default" | "xs" | "sm" | "lg";
const BASE = "inline-flex items-center justify-center whitespace-nowrap text-lg font-body font-semibold border-[2px] border-transparent bg-clip-padding outline-none select-none transition-[color,background,border-color,box-shadow,opacity] motion-reduce:transition-none focus-visible:ring-2 focus-visible:ring-gray-700 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-40 disabled:cursor-not-allowed [&_svg]:pointer-events-none cursor-pointer";
const VARIANTS: Record<Variant, string> = {
  default: "bg-black text-white hover:bg-gray-800",
  primary: "text-white bg-green-700 hover:bg-green-600",
  outline: "border-gray-300 bg-transparent",
  ghost: "bg-transparent hover:underline",
  link: "bg-transparent text-sky-600 underline",
  lightGray: "bg-gray-100 text-black hover:bg-gray-200",
  urgent: "bg-red-600 text-white hover:bg-red-700",
  amber: "bg-amber-500 text-black hover:bg-amber-600",
  subtleGrey: "bg-white text-gray-600 border-gray-300 hover:bg-gray-50",
  expandContent: "bg-brand-100 text-black hover:bg-brand-200"
};
const SIZES: Record<Size, string> = {
  default: "min-h-11 sm:min-h-[50px] min-w-[220px] gap-2 px-[30px] sm:px-[50px] py-[10px]",
  xs: "gap-1 px-2 text-sm",
  sm: "h-[38px] gap-1 border-[1.5px] px-[12px] py-[10px]",
  lg: "h-[45px] gap-1 px-[20px] py-[12px]"
};
interface SharedProps {
  variant?: Variant;
  size?: Size;
}
type AsButton = SharedProps & {
  as?: "button";
} & ComponentProps<"button">;
type AsLink = SharedProps & {
  as: "link";
} & ComponentProps<typeof Link>;
type AsAnchor = SharedProps & {
  as: "anchor";
} & ComponentProps<"a">;
export type ButtonProps = AsButton | AsLink | AsAnchor;
export const Button = t0 => {
  const $ = _c(19);
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      as: t1,
      variant: t2,
      size: t3,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
  }
  const as = t1 === undefined ? "button" : t1;
  const variant = t2 === undefined ? "default" : t2;
  const size = t3 === undefined ? "default" : t3;
  const t4 = VARIANTS[variant];
  const t5 = SIZES[size];
  let t6;
  if ($[6] !== className || $[7] !== t4 || $[8] !== t5) {
    t6 = cn(BASE, t4, t5, className);
    $[6] = className;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const classes = t6;
  if (as === "link") {
    const t7 = props as ComponentProps<typeof Link>;
    let t8;
    if ($[10] !== classes || $[11] !== t7) {
      t8 = <Pill><Link className={classes} {...t7} /></Pill>;
      $[10] = classes;
      $[11] = t7;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    return t8;
  }
  if (as === "anchor") {
    const t7 = props as ComponentProps<"a">;
    let t8;
    if ($[13] !== classes || $[14] !== t7) {
      t8 = <Pill><a className={classes} {...t7} /></Pill>;
      $[13] = classes;
      $[14] = t7;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    return t8;
  }
  const t7 = props as ComponentProps<"button">;
  let t8;
  if ($[16] !== classes || $[17] !== t7) {
    t8 = <Pill><button className={classes} {...t7} /></Pill>;
    $[16] = classes;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
};
export type { Variant as ButtonVariant, Size as ButtonSize };